/**
 * Register page component
 * Handles manual Signup and Google OAuth Signup.
 * @component
 */

import React, { useReducer } from "react";
import Register from "./components/Register";
import { toast } from "react-toastify";
import "./styles/index.css";
import GetOtpForRes from "../../apis/GetOtpForRes";
import { Base64 } from "js-base64";
import SavePass from "../../apis/SavePass";

const initialState = {
  user: { email: "", password: "", c_pass: "" },
  currentStatus: "verifyEmail",
  load: false,
  otp: "",
  otpSent: false,
  otpError: false,
  otpErrorMsg: "",
  showPassword: false,
  showConfirmPassword: false,
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_USER_FIELD":
      return {
        ...state,
        user: { ...state.user, [action.field]: action.value },
      };
    case "SET_OTP":
      return { ...state, otp: action.value };
    case "SET_CURRENT_STATUS":
      return { ...state, currentStatus: action.value };
    case "SET_LOAD":
      return { ...state, load: action.value };
    case "SET_OTP_SENT":
      return { ...state, otpSent: action.value };
    case "SET_OTP_ERROR":
      return {
        ...state,
        otpError: action.error,
        otpErrorMsg: action.msg || "",
      };
    case "TOGGLE_SHOW_PASSWORD":
      return { ...state, showPassword: !state.showPassword };
    case "TOGGLE_SHOW_CONFIRM_PASSWORD":
      return { ...state, showConfirmPassword: !state.showConfirmPassword };
    case "RESET_FORM":
      return { ...initialState };
    default:
      return state;
  }
}

function Signup() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    user,
    currentStatus,
    load,
    otp,
    otpSent,
    otpError,
    otpErrorMsg,
    showPassword,
    showConfirmPassword,
  } = state;

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: "SET_USER_FIELD", field: name, value });
  };

  const handleGlogin = () => {
    window.location.href = `${process.env.REACT_APP_SERVER}/auth/google`;
  };

  const handleVerifyEmail = async () => {
    if (!user.email) {
      toast.error("Please enter your email");
      return false;
    }

    dispatch({ type: "SET_LOAD", value: true });

    try {
      const res = await GetOtpForRes(user.email);
      console.log("res", res);

      if (!res.error) {
        toast.success(`OTP sent to ${user.email}`);
        dispatch({ type: "SET_CURRENT_STATUS", value: "verifyOtp" });
        dispatch({ type: "SET_OTP_SENT", value: true });
        dispatch({ type: "SET_OTP_ERROR", error: false, msg: "" });
        return true;
      } else {
        toast.error(res?.data || "OTP sending failed");
        dispatch({
          type: "SET_OTP_ERROR",
          error: true,
          msg: res?.data?.message || "Error sending OTP",
        });
        return false;
      }
    } catch (err) {
      console.error("OTP sending failed:", err);
      toast.error("Network error while sending OTP");
      dispatch({ type: "SET_OTP_ERROR", error: true, msg: "Network error" });
      return false;
    } finally {
      dispatch({ type: "SET_LOAD", value: false });
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return false;
    }

    dispatch({ type: "SET_LOAD", value: true });

    try {
      const storedOtpEncoded = sessionStorage.getItem("regotp");
      const decodedOtp = Base64.decode(storedOtpEncoded);
      console.log(decodedOtp);

      if (decodedOtp === otp) {
        toast.success("OTP verified");
        dispatch({ type: "SET_CURRENT_STATUS", value: "addPassword" });
        dispatch({ type: "SET_OTP_ERROR", error: false, msg: "" });
        return true;
      } else {
        throw new Error("OTP mismatch");
      }
    } catch (err) {
      toast.error("Invalid OTP");
      dispatch({ type: "SET_OTP_ERROR", error: true, msg: "Invalid OTP" });
      return false;
    } finally {
      dispatch({ type: "SET_LOAD", value: false });
    }
  };

  const handleManualRes = async () => {
    if (!user.password || !user.c_pass) {
      toast.error("Please enter password and confirm it");
      return;
    }
    if (user.password !== user.c_pass) {
      toast.error("Passwords do not match");
      return;
    }

    dispatch({ type: "SET_LOAD", value: true });

    try {
      const res = await SavePass(user.email, user.password);

      if (!res.error) {
        toast.success("Sign up successful! Please login.");
        dispatch({ type: "RESET_FORM" });
      } else {
        toast.error(res.data || "Sign up failed");
      }
    } catch {
      toast.error("Sign up failed");
    } finally {
      dispatch({ type: "SET_LOAD", value: false });
    }
  };

  const onSendOtp = async () => {
    await handleVerifyEmail();
  };

  const onVerifyOtp = async () => {
    const success = await handleVerifyOtp();
    if (!success) {
      dispatch({
        type: "SET_OTP_ERROR",
        error: true,
        msg: "OTP verification failed. Please try again.",
      });
    }
  };

  const onGoBack = () => {
    dispatch({ type: "SET_CURRENT_STATUS", value: "verifyEmail" });
    dispatch({ type: "SET_OTP_SENT", value: false });
    dispatch({ type: "SET_OTP_ERROR", error: false, msg: "" });
    dispatch({ type: "SET_OTP", value: "" });
  };

  return (
    <Register
      currentStatus={currentStatus}
      handleGlogin={handleGlogin}
      handleChange={handleChange}
      handleVerifyEmail={handleVerifyEmail}
      handleVerifyOtp={handleVerifyOtp}
      handleManualRes={handleManualRes}
      user={user}
      otp={otp}
      setOtp={(val) => dispatch({ type: "SET_OTP", value: val })}
      load={load}
      setCurrentStatus={(val) =>
        dispatch({ type: "SET_CURRENT_STATUS", value: val })
      }
      setLoad={(val) => dispatch({ type: "SET_LOAD", value: val })}
      otpSent={otpSent}
      otpError={otpError}
      otpErrorMsg={otpErrorMsg}
      onSendOtp={onSendOtp}
      onVerifyOtp={onVerifyOtp}
      onGoBack={onGoBack}
      showPassword={showPassword}
      showConfirmPassword={showConfirmPassword}
      toggleShowPassword={() => dispatch({ type: "TOGGLE_SHOW_PASSWORD" })}
      toggleShowConfirmPassword={() =>
        dispatch({ type: "TOGGLE_SHOW_CONFIRM_PASSWORD" })
      }
    />
  );
}

export default Signup;
